import { useState } from "react";
import { getAttendanceStatusLabel, TraceType } from "../../constants/constants";
import { parseTimeString } from "../../components/modal/timepicker";

// IST is a fixed UTC+5:30 offset (no DST) — same pattern as Dashboard's
// getISTNow. Break-interval timestamps (attendance.traces[].timestamp) are
// full datetimes, so displaying them with `new Date(ts).toLocaleTimeString()`
// would render in whatever timezone the *browser* happens to be set to,
// not IST. Shifting by the fixed offset and reading fields back out with
// the UTC getters sidesteps the browser's local timezone entirely.
const IST_OFFSET_MINUTES = 5 * 60 + 30;
const toIST = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() + IST_OFFSET_MINUTES * 60000);
};

// 12-hour + AM/PM, e.g. "09:05 AM" — used for full datetime timestamps
// (break start/end) once already shifted into IST by toIST() above.
const formatISTClock = (istDate) => {
  if (!istDate) return "—";
  let hour = istDate.getUTCHours();
  const minute = istDate.getUTCMinutes();
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${ampm}`;
};

// Check-in/Check-out are stored as plain "HH:MM(:SS)" wall-clock time
// (already IST — no date/timezone component to shift), so this just
// reformats the digits into 12-hour + AM/PM, same as the Attendance log
// table, instead of showing the raw "09:05:00" string.
const formatCheckTime = (t) => {
  const parsed = parseTimeString(t);
  if (!parsed) return "—";
  let hour = parsed.hour % 12;
  if (hour === 0) hour = 12;
  const ampm = parsed.hour >= 12 ? "PM" : "AM";
  return `${String(hour).padStart(2, "0")}:${String(parsed.minute).padStart(2, "0")} ${ampm}`;
};

// working_hours / total_break_hours come back from the API as decimal
// hours (e.g. 7.5), which read poorly as "7.5 hrs". Convert to a plain
// "Xh Ym" format instead.
const formatHoursMinutes = (decimalHours) => {
  if (decimalHours === null || decimalHours === undefined || decimalHours === "") return "—";
  const totalMinutes = Math.round(Number(decimalHours) * 60);
  if (Number.isNaN(totalMinutes)) return "—";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
};

const ViewAttendance = ({ attendance, onClose, onEdit }) => {
  const [tab, setTab] = useState("personal");

  if (!attendance) return null;

  const empDetail = attendance.employee_detail || {};

  const hasTraces = attendance.traces && attendance.traces.length > 0;

  const breakIntervals = [];
  if (hasTraces) {
    let currentBreak = null;
    const sortedTraces = [...attendance.traces].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    sortedTraces.forEach(t => {
      if (t.trace_type === TraceType.BREAK_START) {
        currentBreak = { start: t.timestamp, end: null };
        breakIntervals.push(currentBreak);
      } else if (t.trace_type === TraceType.BREAK_END && currentBreak) {
        currentBreak.end = t.timestamp;
        currentBreak = null;
      }
    });
  }

  const formatTime = (ts) => formatISTClock(toIST(ts));

  // Duration between two timestamps is unaffected by timezone (the IST
  // offset cancels out in a subtraction), so this stays exactly as before.
  const calculateDuration = (start, end) => {
    if (!start || !end) return "—";
    const diffMs = new Date(end) - new Date(start);
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours === 0 && mins === 0) return "0m";
    return `${hours > 0 ? hours + 'h ' : ''}${mins}m`;
  };

  return (
    <div className="popup-overlay">
      <div
        className="popup-content premium-profile sticky-shell"
        style={{ maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* STICKY HEADER */}
        <div className="page-header" style={{ marginBottom: 6 }}>
          <h2 style={{ fontSize: 18 }}>Attendance Details</h2>
        </div>
        <hr className="premium-divider" />

        <div className="profile-tabs">
          <button className={tab === "personal" ? "active" : ""} onClick={() => setTab("personal")}>
            Employee Information
          </button>
          <button className={tab === "attendance" ? "active" : ""} onClick={() => setTab("attendance")}>
            Attendance Details
          </button>
        </div>

        {/* SCROLLABLE BODY */}
        <div className="popup-scroll-body">
          {tab === "personal" && (
            <div className="profile-grid">
              <div className="profile-card">
                <label>Emp ID</label>
                <span>{empDetail.emp_id || attendance.emp_id || "—"}</span>
              </div>
              <div className="profile-card">
                <label>Name</label>
                <span>{empDetail.full_name || attendance.employee_name || "—"}</span>
              </div>
              <div className="profile-card">
                <label>Department</label>
                <span>{empDetail.department || attendance.department_name || attendance.department || "—"}</span>
              </div>
              <div className="profile-card">
                <label>Designation</label>
                <span>{empDetail.designation || attendance.designation_name || "—"}</span>
              </div>
            </div>
          )}

          {tab === "attendance" && (
            <>
              <div className="profile-grid">
                <div className="profile-card">
                  <label>Date</label>
                  <span>{attendance.date}</span>
                </div>
                <div className="profile-card">
                  <label>Status</label>
                  <span>{getAttendanceStatusLabel(attendance.status)}</span>
                </div>
                <div className="profile-card">
                  <label>Check-In </label>
                  <span>{formatCheckTime(attendance.check_in_time)}</span>
                </div>
                <div className="profile-card">
                  <label>Check-Out </label>
                  <span>{formatCheckTime(attendance.check_out_time)}</span>
                </div>
                <div className="profile-card">
                  <label>Working Hours</label>
                  <span>{formatHoursMinutes(attendance.working_hours)}</span>
                </div>
                <div className="profile-card">
                  <label>Total Break Time</label>
                  <span>{formatHoursMinutes(attendance.total_break_hours)}</span>
                </div>
                <div className="profile-card">
                  <label>WFH</label>
                  <span>{attendance.is_wfh ? "Yes" : "No"}</span>
                </div>
              </div>

              {attendance.remarks && (
                <div className="profile-card" style={{ marginTop: 16, gridColumn: "1 / -1" }}>
                  <label>Remarks</label>
                  <span style={{ fontWeight: 400 }}>{attendance.remarks}</span>
                </div>
              )}

              {breakIntervals.length > 0 && (
                <div style={{ marginTop: 22 }}>
                  <div className="section-title-premium" style={{ marginBottom: 12 }}>Break Intervals</div>
                  {/*
                    "break-table" is intentionally NOT the shared ".table"
                    class used by the Attendance/Employee lists (those get
                    converted into a 2-column card grid on tablet/mobile
                    via cell-xxx grid-area classes). Each cell below is
                    individually classed (break-slno / break-start /
                    break-end / break-duration) so the mobile CSS can lay
                    each break out as its own card:
                      Sl No: 1
                      02:00 PM - 03:00 PM
                      1h
                    while the desktop view keeps the plain 4-column table.
                  */}
                  <div className="table-responsive break-table-wrap">
                    <table className="table break-table" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>Sl No</th>
                          <th>Start Time</th>
                          <th>End Time</th>
                          <th>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakIntervals.map((b, idx) => (
                          <tr key={idx}>
                            <td className="break-slno">{idx + 1}</td>
                            <td className="break-start">{formatTime(b.start)}</td>
                            <td className="break-end">{formatTime(b.end)}</td>
                            <td className="break-duration">{calculateDuration(b.start, b.end)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* STICKY FOOTER */}
        <div
          className="employee-page-footer"
          style={{ padding: "22px 0 0", margin: "24px 0 0", justifyContent: "flex-end" }}
        >
          <div className="employee-actions">
            <button className="secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAttendance;